import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';

const MinistryArmsSection = ({ darkMode }) => {
  const [ministryData, setMinistryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMinistryData();
  }, []);

  const loadMinistryData = async () => {
    try {
      const docRef = doc(db, 'ministryArmsSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setMinistryData(docSnap.data());
      } else {
        // Fallback to default data if no data in Firestore
        setMinistryData({
          subtitle: "Ministry Expressions",
          title: "The Work of Our Hands",
          description: "Multiple dimensions of ministry service bringing transformation to individuals, families, and communities across Kenya.",
          arms: [
            {
              title: "Founder & Lead Minister",
              desc: "Leading Faith Life Evangelistic Church (FLEC) with a mandate to exalt Christ, equip the saints, and evangelize the world. Establishing growing presence across five Kenyan counties through transformative teachings.",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653742/photo_2025-04-02_16-35-19_h7ut9i.webp"
            },
            {
              title: "Leadership Consultant & Mentor",
              desc: "Over 20 years of experience in organizational leadership, capacity building for ministry workers, and mentorship of next generation leaders. Adept at developing leadership potential in church and parachurch settings.",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653773/DSC_0514_kmus8u.webp"
            },
            {
              title: "Conference Speaker & Counselor",
              desc: "Widely exposed conference and crusade speaker with empathetic counseling skills. Bringing deliverance, healing, prosperity and salvation to many through powerful ministry across Kenya and East Africa.",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653758/photo_2025-03-16_14-41-58_tto0dv.webp"
            },
            {
              title: "Church Planter & Missionary",
              desc: "Extensive experience in church planting across central, northern and lower eastern Kenya, as well as international missions in Zambia, DRC, Tanzania and South Sudan. Trusting God for daily providence in unfamiliar territories.",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653709/photo_2025-05-27_22-36-24_k0kapz.webp"
            },
            {
              title: "Family Man & Spiritual Father",
              desc: "Happily and purposefully married to Pastor Sally Kyengo, blessed with three children: Hadassah, Adah and Shalem. Modeling godly family values while serving as a spiritual father to many.",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653681/shalem__64_t9urqp.webp"
            },
            {
              title: "Educated Theologian",
              desc: "BA in Bible and Theology and MA in Leadership from Pan Africa Christian University, plus international training in Humanitarian Chaplaincy, Leadership, and Cross-Cultural Missions from US institutions.",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653720/photo_2025-04-02_16-35-43_d1fjzu.webp"
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading ministry arms section:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto">
          {/* Header Skeleton */}
          <div className="text-center mb-20 space-y-6">
            <div className={`h-4 bg-slate-400 rounded w-48 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-12 bg-slate-400 rounded w-64 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-8 bg-slate-400 rounded w-96 max-w-full mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
          </div>

          {/* Cards Skeleton */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
              <div key={index} className={`rounded-2xl overflow-hidden border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'} shadow-lg animate-pulse`}>
                <div className={`h-64 bg-slate-400 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                <div className="p-6 space-y-4">
                  <div className={`h-6 bg-slate-400 rounded w-3/4 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                  <div className="space-y-2">
                    <div className={`h-4 bg-slate-400 rounded w-full ${darkMode ? 'bg-slate-600' : ''}`}></div>
                    <div className={`h-4 bg-slate-400 rounded w-5/6 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                    <div className={`h-4 bg-slate-400 rounded w-4/6 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto text-center">
          <div className={`${darkMode ? 'bg-slate-700' : 'bg-white'} p-12 rounded-2xl shadow-lg border ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
            <div className={`text-lg font-bold ${darkMode ? 'text-red-400' : 'text-red-600'} mb-4`}>
              Error Loading Content
            </div>
            <div className={`text-md ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              {error}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Use the actual data from Firestore
  const data = ministryData;

  // MinistryArmCard Component with full image display
  const MinistryArmCard = ({ arm, index, darkMode }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className={`rounded-2xl overflow-hidden border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} shadow-lg hover:shadow-xl transition-all duration-300 group`}
      >
        {/* Image Container - Full image display */}
        <div className="relative h-80 overflow-hidden">
          <motion.img 
            src={arm.img} 
            alt={arm.title}
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              // Fallback if image fails to load
              e.target.src = 'https://res.cloudinary.com/dbacioerk/image/upload/v1763653742/photo_2025-04-02_16-35-19_h7ut9i.webp';
            }}
          />
          {/* Gradient overlay for better text readability */}
          <div className={`absolute inset-0 bg-gradient-to-t ${
            darkMode 
              ? 'from-slate-900/50 to-transparent' 
              : 'from-white/30 to-transparent'
          } transition-opacity duration-300 group-hover:opacity-0`} />
        </div>

        {/* Content */}
        <div className="p-6">
          <motion.h3 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 + 0.2 }}
            className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'} font-sans`}
          >
            {arm.title}
          </motion.h3>
          
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 + 0.3 }}
            className={`leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'} font-sans`}
          >
            {arm.desc}
          </motion.p>
        </div>
      </motion.div>
    );
  };

  return (
    <section className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-4 font-sans`}>
            {data.subtitle || "Ministry Expressions"}
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-4 font-sans`}>
            {data.title || "The Work of Our Hands"}
          </h2>
          <p className={`text-xl ${darkMode ? 'text-slate-300' : 'text-slate-600'} max-w-3xl mx-auto leading-relaxed font-sans`}>
            {data.description || "Multiple dimensions of ministry service bringing transformation to individuals, families, and communities across Kenya."}
          </p>
        </motion.div>

        {/* Ministry Arms Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.arms && data.arms.map((arm, index) => (
            <MinistryArmCard key={index} arm={arm} index={index} darkMode={darkMode} />
          ))}
        </div>

        {/* Fallback if no arms exist */}
        {(!data.arms || data.arms.length === 0) && (
          <div className="text-center py-16">
            <div className={`text-lg ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              No ministry arms content available.
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MinistryArmsSection;